import React, { useState, useEffect } from "react";
import { Box, Drawer, IconButton, CircularProgress } from "@mui/material";
import CustomText, { textStyles } from "../../../../components/CustomText";
import profilecross from "../../../../assets/profilecross.svg";
import document1 from "../../../../assets/document1.svg";
import { useTranslation } from "react-i18next";
import CustomButton from "../../../../components/CustomButton";
import { BASE_URL } from "../../../../components/BaseUrl";
import { decryptToken } from "../../../../Utlis/Encryption";
import { convertUTCToLocalDateTime } from "../../../../components/dateTimeUtils";

const ViewDrawer = ({ open, onClose, row }) => {
  const { t } = useTranslation();
  const [documentData, setDocumentData] = useState(null);
  const [loading, setLoading] = useState(false);
  
  const rawEncryptedToken = localStorage.getItem("token");
  const token = rawEncryptedToken ? decryptToken(rawEncryptedToken) : null;

  const formatDateTime = (dateString) => {
    if (!dateString) return "-";
    const localDateTime = convertUTCToLocalDateTime(dateString, {
      locale: "en-GB",
      format: "DD MMM YYYY",
    });
    return localDateTime === "—" ? "-" : localDateTime;
  };

  const InfoBox = ({ children }) => (
    <Box
      mt={2}
      sx={{
        border: "1px solid #091E4224",
        borderRadius: "10px",
        px: 2,
        py: 2,
      }}
    >
      {children}
    </Box>
  );

  const InfoRow = ({ label, value }) => (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        mb: 2,
      }}
    >
      <CustomText
        sx={{
          ...textStyles.h1,
          fontSize: 16,
          fontWeight: 400,
          color: "#5E5C5C",
        }}
      >
        {label}
      </CustomText>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        {value}
      </Box>
    </Box>
  );

  // Fetch single document when drawer opens
  useEffect(() => {
    if (open && row?.id && token) {
      setLoading(true);
      fetch(`${BASE_URL}/documents/${row.id}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      })
        .then((res) => res.json())
        .then((data) => {
          if (data.status === "success" && data.data?.document) {
            setDocumentData(data.data.document);
          } else {
            // Fallback to row data if API fails
            setDocumentData(row);
          }
        })
        .catch((err) => {
          console.error("❌ Error fetching document:", err);
          // Fallback to row data
          setDocumentData(row);
        })
        .finally(() => setLoading(false));
    } else if (open && row) {
      // Use row data directly if no ID or token
      setDocumentData(row);
    }
  }, [open, row?.id, token]);

  if (!row) return null;
  
  const doc = documentData || row;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: { width: 500, backgroundColor: "white" },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 10,
          backgroundColor: "#E7EBEE",
          p: 2,
          py: 1,
          borderBottom: "1px solid #eee",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <IconButton onClick={onClose}>
            <img src={profilecross} alt="close" />
          </IconButton>
          <CustomText
            sx={{
              fontSize: 20,
              fontWeight: 600,
              color: "#2C384C",
              fontFamily: "Poppins, sans-serif",
            }}
          >
           {t("documentsHeading")} #{doc.id}
          </CustomText>
        </Box>
      </Box>

      {/* Content */}
      <Box sx={{ px: 2, pb: 2, mt: 2 }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <InfoBox>
              <InfoRow 
                label={t("documents.th.id") || "ID"} 
                value={doc.id || "-"} 
              />

              <InfoRow 
                label={t("documents.th.documentName") || "Document Name"} 
                value={doc.name || "-"} 
              />
                 <InfoRow 
                label={t("documents.th.description") || "Document Name"} 
                value={doc.description || "-"} 
              />

     <InfoRow 
                label={t("documents.th.subject") || "Document Name"} 
                value={doc.subject || "-"} 
              />
          

              <InfoRow
                label={t("documents.th.fileType") || "File Type"}
                value={
                  (doc.file_type || doc.fileType || "").toLowerCase().includes("pdf") ? (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <img src={document1} alt="pdf" style={{ width: 24, height: 24 }} />
                      <CustomText
                        sx={{
                          ...textStyles.h1,
                          fontSize: 15,
                          fontWeight: 400,
                          color: "#172B4D",
                          textTransform: "uppercase",
                        }}
                      >
                        {doc.file_type || doc.fileType || "-"}
                      </CustomText>
                    </Box>
                  ) : (
                    <CustomText
                      sx={{
                        ...textStyles.h1,
                        fontSize: 15,
                        fontWeight: 400,
                        color: "#172B4D",
                        textTransform: "uppercase",
                      }}
                    >
                      {doc.file_type || doc.fileType || "-"}
                    </CustomText>
                  )
                }
              />

              {doc.category && (
                <InfoRow 
                  label={t("documents.th.category") || "Category"} 
                  value={doc.category} 
                />
              )}

              {doc.owner && (
                <InfoRow 
                  label={t("documents.owner") || "Owner"} 
                  value={`${doc.owner.name || doc.owner.email || "-"} (${doc.owner.type || "-"})`} 
                />
              )}

              <InfoRow 
                label={t("documents.th.uploadedAt") || "Uploaded At"} 
                value={formatDateTime(doc.created_at || doc.createdAt)} 
              />

              {doc.updated_at && doc.updated_at !== doc.created_at && (
                <InfoRow 
                  label={t("documents.th.updatedAt") || "Updated At"} 
                  value={formatDateTime(doc.updated_at)} 
                />
              )}
            </InfoBox>

            {/* Document Preview Box */}
            <InfoBox>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  background: "#f9f9f9",
                  borderRadius: "6px",
                  p: 3,
                  width: "100%",
                  minHeight: 200,
                  textAlign: "center",
                }}
              >
                {/* Document Icon */}
                <img
                  src={document1}
                  alt="Document"
                  style={{ width: 80, height: 80, marginBottom: 12 }}
                />
                
                {/* Document Name */}
                <CustomText
                  sx={{
                    fontSize: 14,
                    fontWeight: 500,
                    color: "#172B4D",
                    textAlign: "center",
                    wordBreak: "break-word",
                    mb: 2,
                    maxWidth: "100%",
                  }}
                >
                  {doc.name || "Document"}
                </CustomText>

              </Box>
            </InfoBox>
            
            {/* View Document Button - Always visible */}
            <Box sx={{ textAlign: "center", mt: 3, width: "100%" }}>
              <CustomButton
                label={t("requestDetails.viewDocument") || "View Document"}
                size="large"
                variant="contained"
                onClick={() => {
                  // Get document URL from either field name
                  const docUrl = doc.document_url || doc.documentUrl;
                  
                  // Construct the view URL
                  let viewUrl;
                  
                  if (docUrl) {
                    if (docUrl.startsWith('http://') || docUrl.startsWith('https://')) {
                      // Full URL - use directly
                      viewUrl = docUrl;
                    } else if (docUrl.startsWith('/')) {
                      // Relative path - construct full URL
                      const baseUrl = BASE_URL.replace('/api', '');
                      viewUrl = `${baseUrl}${docUrl}`;
                    } else {
                      // Document identifier or file path - try to construct URL
                      // First try as relative path
                      viewUrl = `${BASE_URL.replace('/api', '')}${docUrl.startsWith('/') ? docUrl : '/' + docUrl}`;
                    }
                  } else {
                    // No document_url - use view endpoint with document ID
                    viewUrl = `${BASE_URL}/documents/${doc.id}/view`;
                  }
                  
                  console.log("🔗 Opening document URL:", viewUrl);
                  console.log("📄 Document data:", doc);
                  
                  // Open in new tab
                  window.open(viewUrl, "_blank", "noopener,noreferrer");
                }}
                sx={{
                  minWidth: 250,
                  px: 4,
                  py: 1.5,
                  fontSize: 16,
                  fontWeight: 600,
                }}
              />
            </Box>
          </>
        )}
      </Box>
    </Drawer>
  );
};

export default ViewDrawer;